import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { getServiceProducts, ServiceProduct } from '../../services/serviceProductService';
import * as paymentMethodService from '../../services/paymentMethodService';

interface ServiceProductSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectService: (serviceProduct: ServiceProduct, paymentMethodId?: string) => void;
  paymentMethods?: paymentMethodService.PaymentMethod[];
}

interface SelectedPayment {
  serviceId: string;
  paymentMethodId: string;
}

const ServiceProductSearchDialog: React.FC<ServiceProductSearchDialogProps> = ({
  open,
  onClose,
  onSelectService,
  paymentMethods = []
}) => {
  const [serviceProducts, setServiceProducts] = useState<ServiceProduct[]>([]);
  const [filteredServices, setFilteredServices] = useState<ServiceProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPayments, setSelectedPayments] = useState<SelectedPayment[]>([]);
  const [calculatingFees, setCalculatingFees] = useState<string | null>(null);

  // تحميل خدمات المنتج عند فتح النافذة
  useEffect(() => {
    if (open) {
      loadServiceProducts();
    }
  }, [open]);

  // تصفية خدمات المنتج حسب البحث
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredServices(serviceProducts);
    } else {
      const filtered = serviceProducts.filter(service =>
        service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.barcode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (service.notes && service.notes.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredServices(filtered);
    }
  }, [searchTerm, serviceProducts]);

  const loadServiceProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getServiceProducts();
      const activeServices = response.data.filter((service: ServiceProduct) => service.status === 'active');
      setServiceProducts(activeServices);
      setFilteredServices(activeServices);
    } catch (err) {
      console.error('Error loading service products:', err);
      setError('حدث خطأ أثناء تحميل خدمات المنتج');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectService = async (serviceProduct: ServiceProduct) => {
    const selectedPayment = selectedPayments.find(p => p.serviceId === serviceProduct._id);
    onSelectService(serviceProduct, selectedPayment?.paymentMethodId);
    onClose();
    setSearchTerm(''); // إعادة تعيين البحث
    setSelectedPayments([]); // إعادة تعيين اختيارات طرق الدفع
  };

  const handlePaymentMethodChange = async (serviceId: string, paymentMethodId: string) => {
    setSelectedPayments(prev => {
      const existing = prev.findIndex(p => p.serviceId === serviceId);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { serviceId, paymentMethodId };
        return updated;
      }
      return [...prev, { serviceId, paymentMethodId }];
    });
  };

  const getSelectedPaymentMethod = (serviceId: string) => {
    const selected = selectedPayments.find(p => p.serviceId === serviceId);
    if (!selected || !selected.paymentMethodId) return null;
    return paymentMethods.find(m => m.id === selected.paymentMethodId) || null;
  };

  const handleClose = () => {
    onClose();
    setSearchTerm(''); // إعادة تعيين البحث عند الإغلاق
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">اختيار خدمة منتج</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* شريط البحث */}
        <Box mb={2}>
          <TextField
            fullWidth
            placeholder="البحث في خدمات المنتج..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
        </Box>

        {/* محتوى النافذة */}
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : filteredServices.length === 0 ? (
          <Alert severity="info">
            {searchTerm ? 'لا توجد خدمات تطابق البحث' : 'لا توجد خدمات منتج متاحة'}
          </Alert>
        ) : (
          <Paper elevation={1}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>اسم الخدمة</TableCell>
                  <TableCell>الباركود</TableCell>
                  <TableCell>السعر</TableCell>
                  <TableCell>طريقة الدفع</TableCell>
                  <TableCell>الوصف</TableCell>
                  <TableCell align="center">إجراء</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredServices.map((service) => {
                  const selectedPaymentMethod = getSelectedPaymentMethod(service._id);
                  return (
                    <TableRow key={service._id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Typography variant="body2" fontWeight="medium">
                            {service.name}
                          </Typography>
                          <Chip
                            label="خدمة"
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {service.barcode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {service.price.toFixed(2)} ر.س
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <InputLabel>طريقة الدفع</InputLabel>
                          <Select
                            value={selectedPayments.find(p => p.serviceId === service._id)?.paymentMethodId || ''}
                            label="طريقة الدفع"
                            onChange={(e) => handlePaymentMethodChange(service._id, e.target.value)}
                            startAdornment={
                              <InputAdornment position="start">
                                <PaymentIcon fontSize="small" />
                              </InputAdornment>
                            }
                          >
                            <MenuItem value="">
                              <em>بدون</em>
                            </MenuItem>
                            {paymentMethods
                              .filter((method) => {
                                const linkedId = typeof method.linkedServiceProductId === 'object'
                                  ? method.linkedServiceProductId?._id
                                  : method.linkedServiceProductId;
                                return linkedId === service._id;
                              })
                              .map((method) => (
                                <MenuItem key={method.id} value={method.id}>
                                  {method.name}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {service.notes || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => handleSelectService(service)}
                          sx={{ minWidth: '80px' }}
                        >
                          إضافة
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          إلغاء
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceProductSearchDialog;